import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, User, Store, Truck, CheckCircle, ArrowRight, ArrowDown, Package, CreditCard, Search, MapPin, Phone, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const steps = [
  {
    id: 'customer',
    role: 'Customer',
    icon: User,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50 border-blue-200',
    textColor: 'text-blue-700',
    title: 'Customer Places Order',
    screens: [
      {
        label: 'Browse Products',
        desc: 'Customer browses 88+ grocery products across 9 categories on narkonline.com',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-stone-700">
              <Search className="w-4 h-4" />
              <span>Search products...</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {['Coca-Cola 2.25L', 'Albany Bread', 'Lucky Star'].map(item => (
                <div key={item} className="bg-stone-50 rounded-lg p-2 text-center">
                  <div className="w-full aspect-square bg-stone-200 rounded mb-1" />
                  <p className="text-xs font-medium truncate">{item}</p>
                  <p className="text-xs text-green-700 font-bold">R24.20</p>
                </div>
              ))}
            </div>
          </div>
        )
      },
      {
        label: 'Add to Cart & Pay',
        desc: 'Items are added to cart. At checkout, payment is made securely via Paystack. R30 delivery fee applied.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Cart (3 items)</span>
              <span className="font-bold text-green-700">R72.60</span>
            </div>
            <div className="flex items-center justify-between text-sm text-stone-500">
              <span>Delivery fee</span>
              <span>R30.00</span>
            </div>
            <div className="border-t pt-2 flex items-center justify-between font-bold">
              <span>Total</span>
              <span className="text-green-700">R102.60</span>
            </div>
            <div className="flex items-center gap-2 bg-blue-50 rounded-lg p-3">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Pay securely with Paystack</span>
            </div>
          </div>
        )
      }
    ]
  },
  {
    id: 'admin',
    role: 'Admin',
    icon: Package,
    color: 'bg-green-800',
    lightColor: 'bg-green-50 border-green-200',
    textColor: 'text-green-800',
    title: 'Admin Receives & Assigns',
    screens: [
      {
        label: 'Order Notification',
        desc: 'Admin receives WhatsApp notification + email with order details. Reviews the order in the dashboard.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <div className="flex items-center gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
              <Phone className="w-5 h-5 text-green-700" />
              <div>
                <p className="text-xs text-green-600">WhatsApp Alert</p>
                <p className="text-sm font-medium text-green-900">New order #4f8a2b - R102.60</p>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 bg-orange-50 rounded-lg p-3 border border-orange-200 text-center">
                <p className="text-xs text-orange-600">Status</p>
                <p className="text-sm font-bold text-orange-800">Paid</p>
              </div>
              <div className="flex-1 bg-blue-50 rounded-lg p-3 border border-blue-200 text-center">
                <p className="text-xs text-blue-600">Items</p>
                <p className="text-sm font-bold text-blue-800">3 products</p>
              </div>
            </div>
          </div>
        )
      },
      {
        label: 'Assign to Shopper',
        desc: 'Admin assigns the order to an available shopper via round-robin. The shopper gets notified.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <p className="text-sm font-medium text-stone-700">Assign order to:</p>
            <div className="flex gap-2">
              <button className="flex-1 bg-green-900 text-white rounded-lg p-3 text-sm font-medium">
                Assign to Shopper
              </button>
            </div>
            <div className="flex items-center gap-2 bg-green-50 rounded p-2 text-sm text-green-800">
              <CheckCircle className="w-4 h-4" />
              <span>Assigned to next available shopper (round-robin)</span>
            </div>
          </div>
        )
      }
    ]
  },
  {
    id: 'shopper',
    role: 'Shopper',
    icon: Store,
    color: 'bg-purple-600',
    lightColor: 'bg-purple-50 border-purple-200',
    textColor: 'text-purple-700',
    title: 'Shopper Sources Items',
    screens: [
      {
        label: 'Select Store',
        desc: 'Shopper chooses which store to source from (Pick n Pay, Checkers, Shoprite, etc.)',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <p className="text-sm font-medium text-stone-700">Choose store:</p>
            <div className="space-y-2">
              {['Pick n Pay', 'Checkers', 'Shoprite'].map(store => (
                <button key={store} className="w-full text-left bg-stone-50 hover:bg-purple-50 border rounded-lg p-3 text-sm font-medium transition-colors">
                  {store}
                </button>
              ))}
            </div>
          </div>
        )
      },
      {
        label: 'Find Items in Store',
        desc: 'Shopper goes to the physical store and ticks off each item as found. Items not available are marked for refund.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-2">
            <p className="text-sm font-medium text-stone-700 mb-2">Shopping list:</p>
            {[
              { name: 'Coca-Cola 2.25L', found: true },
              { name: 'Albany Bread 700g', found: true },
              { name: 'Lucky Star Pilchards', found: false },
            ].map(item => (
              <div key={item.name} className={`flex items-center justify-between p-2 rounded-lg text-sm ${item.found ? 'bg-green-50' : 'bg-red-50'}`}>
                <span className={item.found ? 'text-green-800' : 'text-red-800'}>{item.name}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.found ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                  {item.found ? 'Found' : 'Not Found'}
                </span>
              </div>
            ))}
            <div className="pt-2 border-t mt-2">
              <p className="text-xs text-stone-500">Items not found → refund request sent to admin</p>
            </div>
          </div>
        )
      }
    ]
  },
  {
    id: 'refund',
    role: 'Admin',
    icon: Package,
    color: 'bg-yellow-600',
    lightColor: 'bg-yellow-50 border-yellow-200',
    textColor: 'text-yellow-700',
    title: 'Admin Approves Refund',
    screens: [
      {
        label: 'Refund Approval',
        desc: 'If items are unavailable, admin reviews and approves the partial refund. Customer is notified and refunded automatically.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm font-medium text-yellow-800">Refund Request</p>
              <p className="text-xs text-yellow-600 mt-1">Lucky Star Pilchards - R25.30</p>
            </div>
            <div className="flex gap-2">
              <button className="flex-1 bg-green-700 text-white rounded-lg p-2 text-sm font-medium">
                Approve Refund
              </button>
              <button className="flex-1 bg-stone-200 text-stone-700 rounded-lg p-2 text-sm font-medium">
                Decline
              </button>
            </div>
            <p className="text-xs text-stone-500">Customer receives email: "R25.30 refunded for Lucky Star Pilchards"</p>
          </div>
        )
      }
    ]
  },
  {
    id: 'driver',
    role: 'Driver',
    icon: Truck,
    color: 'bg-orange-600',
    lightColor: 'bg-orange-50 border-orange-200',
    textColor: 'text-orange-700',
    title: 'Driver Delivers Order',
    screens: [
      {
        label: 'Accept Delivery',
        desc: 'Once the shopper has sourced all items, the order appears on the driver dashboard. Driver accepts and picks up.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Order #4f8a2b</p>
                <p className="text-xs text-stone-500">123 Kerk St, Eersterust</p>
              </div>
              <span className="text-sm font-bold text-green-700">R77.30</span>
            </div>
            <div className="flex gap-2">
              <button className="flex-1 bg-green-800 text-white rounded-lg p-2 text-sm font-medium">
                Accept
              </button>
              <button className="flex-1 bg-stone-100 text-stone-700 rounded-lg p-2 text-sm font-medium">
                Decline
              </button>
            </div>
          </div>
        )
      },
      {
        label: 'Deliver to Customer',
        desc: 'Driver picks up from shopper and delivers to the customer\'s door via tuk-tuk. Marks as delivered.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <div className="flex items-center gap-3 bg-orange-50 rounded-lg p-3 border border-orange-200">
              <MapPin className="w-5 h-5 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-orange-900">Delivering to:</p>
                <p className="text-xs text-orange-700">123 Kerk St, Eersterust</p>
              </div>
            </div>
            <button className="w-full bg-green-700 text-white rounded-lg p-3 text-sm font-bold">
              Mark as Delivered
            </button>
          </div>
        )
      }
    ]
  },
  {
    id: 'complete',
    role: 'Customer',
    icon: Star,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50 border-blue-200',
    textColor: 'text-blue-700',
    title: 'Customer Receives & Rates',
    screens: [
      {
        label: 'Order Delivered!',
        desc: 'Customer receives their groceries. They can track the entire journey in real-time via the orders page.',
        visual: (
          <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-700" />
            </div>
            <h3 className="font-bold text-green-900">Order Delivered!</h3>
            <p className="text-sm text-stone-500">Your groceries have arrived</p>
            <div className="flex justify-center gap-1">
              {[1,2,3,4,5].map(s => (
                <Star key={s} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-xs text-stone-500">Rate your experience</p>
          </div>
        )
      }
    ]
  }
];

const HowItWorksPage = () => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 bg-stone-50">
        {/* Hero */}
        <section className="bg-green-900 text-white py-12 text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">How NarkOnline Works</h1>
            <p className="text-green-100 text-base max-w-xl mx-auto">
              From order to doorstep — see how every role works together to deliver your groceries
            </p>
          </div>
        </section>

        {/* Role pills */}
        <section className="py-6 bg-white border-b border-stone-200 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-center gap-2">
            {steps.map((step, idx) => (
              <button
                key={step.id + idx}
                onClick={() => {
                  setActiveStep(idx);
                  document.getElementById(`step-${idx}`).scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeStep === idx
                    ? `${step.color} text-white shadow-lg`
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                <step.icon className="w-4 h-4" />
                {step.role}{idx === 3 ? ' (Refund)' : ''}
              </button>
            ))}
          </div>
        </section>

        {/* Steps flow */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 space-y-8">
            {steps.map((step, idx) => (
              <div
                key={step.id + idx}
                id={`step-${idx}`}
                className={`rounded-2xl border-2 p-6 transition-all ${
                  activeStep === idx ? step.lightColor + ' shadow-lg' : 'bg-white border-stone-200'
                }`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-full ${step.color} text-white flex items-center justify-center`}>
                    <step.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wider ${step.textColor}`}>
                      Step {idx + 1} — {step.role}
                    </p>
                    <h2 className="text-lg font-bold text-stone-900">{step.title}</h2>
                  </div>
                </div>

                <div className={`grid ${step.screens.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md'} gap-6`}>
                  {step.screens.map((screen) => (
                    <div key={screen.label}>
                      <h3 className="font-semibold text-stone-900 text-sm mb-1">{screen.label}</h3>
                      <p className="text-sm text-stone-500 mb-3 leading-relaxed">{screen.desc}</p>
                      {screen.visual}
                    </div>
                  ))}
                </div>

                {idx < steps.length - 1 && (
                  <div className="flex justify-center mt-6">
                    <ArrowDown className="w-6 h-6 text-stone-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-green-900 text-white text-center">
          <div className="max-w-2xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-3">Ready to Order?</h2>
            <p className="text-green-100 mb-6">Browse our catalogue and get fresh groceries delivered to your door today.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/products">
                <Button className="bg-white text-green-900 hover:bg-green-50 rounded-full px-8 py-5 text-base font-semibold">
                  Start Shopping
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/driver/signup">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-5 text-base">
                  Become a Driver
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorksPage;
